// btw-front/src/pages/PartnerProfile.jsx
// 내 정보 — 로그인한 계정의 이름·연락처·비밀번호 수정.
// 같은 회사 계정은 모두 동등하다(대표 구분 없음). 회사 정보·영업대표 목록은 /partners/edit 에서 관리.
// 이메일·로그인 ID 는 여기서 바꾸지 않는다(이메일은 비밀번호 재설정 수신처라 관리자 경로).
// 영업대표 목록의 본인 행은 계정 정보와 별개라 이 화면에서 저장해도 바뀌지 않는다.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { getMe, updateMe } from "../api/partnerDeals";
import { usePartnerSession } from "../context/PartnerSessionContext";
import { formatPhone } from "../lib/format";
import { PASSWORD_RE } from "../lib/partnerSignupValidation";

const EMPTY_PASSWORD = { currentPassword: "", newPassword: "", newPasswordConfirm: "" };

const serverMessage = (err) =>
  (err && err.payload && err.payload.error && err.payload.error.message) || null;

export default function PartnerProfile() {
  const { t } = useTranslation();
  const { profile, setProfile } = usePartnerSession();
  const [loading, setLoading] = useState(true);
  // 조회 실패 시 폼을 띄우지 않는다 — 빈 칸 상태로 저장하면 기존 연락처가 지워진다
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [account, setAccount] = useState({ loginId: "", email: "" });
  const [info, setInfo] = useState({ name: "", phone: "" });
  // 불러온 값 — 저장 시 바뀐 칸만 보낸다(안 건드린 칸이 의도치 않게 덮이지 않도록)
  const [loadedInfo, setLoadedInfo] = useState({ name: "", phone: "" });
  const [savingInfo, setSavingInfo] = useState(false);
  const [pw, setPw] = useState(EMPTY_PASSWORD);
  const [pwErrors, setPwErrors] = useState({});
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    getMe()
      .then((me) => {
        if (cancelled) return;
        const u = (me && me.user) || {};
        const loaded = { name: u.name || "", phone: u.phone || "" };
        setAccount({ loginId: u.loginId || "", email: u.email || "" });
        setInfo(loaded);
        setLoadedInfo(loaded);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
        message.error(t("partners.profile.loadFailed"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t, reloadKey]);

  async function saveInfo() {
    if (savingInfo) return;
    const name = info.name.trim();
    if (!name) {
      message.error(t("partners.profile.nameRequired"));
      return;
    }
    const phone = info.phone.trim();
    const payload = {};
    if (name !== loadedInfo.name) payload.name = name;
    if (phone !== loadedInfo.phone) payload.phone = phone;
    if (!Object.keys(payload).length) {
      message.info(t("partners.profile.noChanges"));
      return;
    }
    setSavingInfo(true);
    try {
      await updateMe(payload);
      setLoadedInfo({ name, phone });
      // 화면 상단 인사말·메뉴의 이름도 바로 바뀌도록 세션 프로필 갱신
      setProfile({ ...profile, name });
      message.success(t("partners.profile.saved"));
    } catch (err) {
      message.error(serverMessage(err) || t("partners.profile.saveFailed"));
    } finally {
      setSavingInfo(false);
    }
  }

  async function savePassword() {
    if (savingPw) return;
    const e = {};
    if (!pw.currentPassword) e.currentPassword = t("partners.profile.currentPasswordRequired");
    if (!PASSWORD_RE.test(pw.newPassword)) e.newPassword = t("partners.profile.passwordRule");
    if (pw.newPassword !== pw.newPasswordConfirm)
      e.newPasswordConfirm = t("partners.profile.passwordMismatch");
    setPwErrors(e);
    if (Object.keys(e).length) return;
    setSavingPw(true);
    try {
      await updateMe({ currentPassword: pw.currentPassword, newPassword: pw.newPassword });
      setPw(EMPTY_PASSWORD);
      message.success(t("partners.profile.passwordChanged"));
    } catch (err) {
      if (err && err.code === "INVALID_CURRENT_PASSWORD") {
        setPwErrors({ currentPassword: t("partners.profile.currentPasswordWrong") });
      } else {
        message.error(serverMessage(err) || t("partners.profile.saveFailed"));
      }
    } finally {
      setSavingPw(false);
    }
  }

  const setPwField = (key) => (ev) => {
    const value = ev.target.value;
    setPw((p) => ({ ...p, [key]: value }));
    setPwErrors((errs) => ({ ...errs, [key]: undefined }));
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          <div className="mx-auto max-w-2xl">
            <header className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
                {t("partners.profile.title")}
              </h1>
              <Link to="/partners/dashboard" className="btw-btn-ghost">
                {t("partners.profile.back")}
              </Link>
            </header>

            {loading ? (
              <p className="mt-10 text-center text-sm text-slate-400">
                {t("partners.common.loading")}
              </p>
            ) : loadFailed ? (
              <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 text-center">
                <p className="text-sm text-slate-600">{t("partners.profile.loadFailedHint")}</p>
                <button
                  type="button"
                  className="btw-btn-ghost mt-4"
                  onClick={() => setReloadKey((k) => k + 1)}
                >
                  {t("partners.profile.retry")}
                </button>
              </div>
            ) : (
              <div className="mt-6 grid min-w-0 gap-6">
                <section className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
                  <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t("partners.profile.sectionInfo")}
                  </h2>
                  <fieldset
                    disabled={savingInfo}
                    className="mt-3 grid min-w-0 gap-4 md:grid-cols-2"
                  >
                    <Input
                      label={t("partners.profile.loginId")}
                      value={account.loginId}
                      disabled
                      inputClassName="disabled:bg-slate-100 disabled:text-slate-500"
                    />
                    <Input
                      label={t("partners.profile.email")}
                      value={account.email}
                      disabled
                      hint={t("partners.profile.emailHint")}
                      inputClassName="disabled:bg-slate-100 disabled:text-slate-500"
                    />
                    <Input
                      label={t("partners.profile.name")}
                      required
                      value={info.name}
                      onChange={(e) => setInfo((v) => ({ ...v, name: e.target.value }))}
                      maxLength={100}
                    />
                    <Input
                      label={t("partners.profile.phone")}
                      value={info.phone}
                      onChange={(e) =>
                        setInfo((v) => ({ ...v, phone: formatPhone(e.target.value) }))
                      }
                      maxLength={13}
                      inputMode="numeric"
                    />
                  </fieldset>
                  <p className="mt-3 px-1 text-xs text-slate-500">
                    {t("partners.profile.salesRepHint")}
                  </p>
                  <div className="mt-4 flex justify-end">
                    <Button variant="accent" onClick={saveInfo} disabled={savingInfo}>
                      {savingInfo
                        ? t("partners.deals.form.submitting")
                        : t("partners.profile.save")}
                    </Button>
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
                  <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t("partners.profile.sectionPassword")}
                  </h2>
                  <fieldset disabled={savingPw} className="mt-3 grid min-w-0 gap-4">
                    <Input
                      label={t("partners.profile.currentPassword")}
                      type="password"
                      autoComplete="current-password"
                      value={pw.currentPassword}
                      onChange={setPwField("currentPassword")}
                      error={pwErrors.currentPassword}
                    />
                    <Input
                      label={t("partners.profile.newPassword")}
                      type="password"
                      autoComplete="new-password"
                      value={pw.newPassword}
                      onChange={setPwField("newPassword")}
                      hint={t("partners.profile.passwordRule")}
                      error={pwErrors.newPassword}
                    />
                    <Input
                      label={t("partners.profile.newPasswordConfirm")}
                      type="password"
                      autoComplete="new-password"
                      value={pw.newPasswordConfirm}
                      onChange={setPwField("newPasswordConfirm")}
                      error={pwErrors.newPasswordConfirm}
                    />
                  </fieldset>
                  <div className="mt-4 flex justify-end">
                    <Button variant="accent" onClick={savePassword} disabled={savingPw}>
                      {savingPw
                        ? t("partners.deals.form.submitting")
                        : t("partners.profile.changePassword")}
                    </Button>
                  </div>
                </section>
              </div>
            )}
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
}
